// ─── Types ─────────────────────────────────────────────────────────────────────

export interface CreateCrtForm {
  crt: string
  crtStatus: string
  client: string
  shipperCity: string
  recipientCity: string
  driverName: string
  truckPlate: string
  trailerPlate: string
  loadingDate: string
}

export const EMPTY_CREATE: CreateCrtForm = {
  crt: "", crtStatus: "Abierto", client: "", shipperCity: "",
  recipientCity: "", driverName: "", truckPlate: "", trailerPlate: "", loadingDate: "",
}

export interface CrtRow {
  id: string
  crt: string
  borderCrossing: string | null
  shipperLocation: string | null
  shipperCity: string | null
  shipperCountry: string | null
  recipientLocation: string | null
  recipientCity: string | null
  recipientCountry: string | null
  client: string | null
  matchedClientId: string | null
  product: string | null
  matchedProductId: string | null
  loadingCity: string | null
  matchedShipperCityId: string | null
  matchedLoadingCityId: string | null
  matchedRecipientCityId: string | null
  unloadingCity: string | null
  unloadingCountry: string | null
  matchedUnloadingCityId: string | null
  carrier: string | null
  statusConfig: string | null
  freight: string | null
  matchedRouteId: string | null
  matchedRouteKm: number | null
  crtStatus: string
  isManual: boolean
  deliveryStatus: string | null
  warningCount: number | null
  detailCount: number
  collectionArrival: string | null
  createdAt: string
  updatedAt: string
  notes: string | null
  // Flag backend: Regla 3a (carrier TransJovem + owner CDC/Kaizen) → reemplaza cliente por TransJovem
  tjRule3?: boolean
  // Flag backend: Regla 3a(bis) (carrier TransJovem + owner TJ) → excluido de carga y documentos
  tjRule3bis?: boolean
}

export interface DetailRow {
  id: string
  sourceId: string
  crt: string
  matchedDriverId: string | null
  matchedTruckId: string | null
  matchedTrailerId: string | null
  driverName: string | null
  truckPlate: string | null
  truckOwner: string | null
  cargoPosition: string | null
  weight: string | null
  driverPhone: string | null
  expectedDate: string | null
  trailerPlate: string | null
  loadingDate: string | null
  unloadingDate: string | null
  positionType: string | null
  formula: string | null
  driverAdvanceTotal: string | null
  currentStatus: string | null
  collectionScheduleDate: string | null
  storageDays: number | null
  collectionArrival: string | null
  collectionDeparture: string | null
  expectedArrivalBorder: string | null
  passwordAnticipation: string | null
  borderArrival: string | null
  authorizedCrossing: string | null
  passwordRequest: string | null
  customsOriginEntry: string | null
  customsOriginExit: string | null
  customsTransitEntry: string | null
  customsTransitExit: string | null
  expectedCustomsDestination: string | null
  customsDestinationEntry: string | null
  customsDestinationExit: string | null
  expectedUnloadingArrival: string | null
  unloadingDeparture: string | null
  unloadingArrival: string | null
  createdAt: string
  updatedAt: string
}

export interface CrtFullRow {
  id: string
  crt: string
  borderCrossing: string | null
  shipperLocation: string | null
  shipperCity: string | null
  shipperCountry: string | null
  recipientLocation: string | null
  recipientCity: string | null
  recipientCountry: string | null
  currency: string | null
  freight: string | null
  seal: string | null
  client: string | null
  clientSimpleName: string | null
  product: string | null
  carrier: string | null
  loadingLocation: string | null
  loadingCity: string | null
  loadingCountry: string | null
  unloadingLocation: string | null
  unloadingCity: string | null
  unloadingCountry: string | null
  matchedLoadingCityId: string | null
  matchedUnloadingCityId: string | null
  matchedShipperCityId: string | null
  matchedRecipientCityId: string | null
  reference: string | null
  commercialInvoice: string | null
  fobValue: string | null
  customsOrigin: string | null
  customsDestination: string | null
  noteDanfe: string | null
  freightContract: string | null
  fleetLabel: string | null
  cargoControlObs: string | null
  endorsementProtocol: string | null
  queryType: string | null
  danfe: string | null
  notes: string | null
  statusConfig: string | null
  crtStatus: string
  createdAt: string
  updatedAt: string
}

export interface HeaderValidationResult {
  validateOnly: true
  headerDiff: {
    ok: boolean
    expectedCount: number
    foundCount: number
    mismatches: Array<{ index: number; expected: string; found: string }>
  } | null
}

export interface ImportResult {
  imported: number
  updated: number
  unchanged: number
  warnings:  Array<{ sourceId: string; crt: string; fields: string[] }>
  rowErrors: Array<{ line: number; crt: string; sourceId: string; error: string }>
  headerDiff?: {
    ok: boolean
    expectedCount: number
    foundCount: number
    mismatches: Array<{ index: number; expected: string; found: string }>
  }
}

export interface CrtsResponse {
  data: CrtRow[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export interface ExportRules {
  cargoData: {
    groupByCrt: boolean
  }
  tripData: {
    mergeByDriver: boolean
    mergeMaxDaysDiff: number
    replaceTJClient: boolean
    applyFleteroRules: boolean
  }
}

export type SortCol = "crt" | "detail_count" | "warning_count" | "collection_arrival" | "delivery_status"
